import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import StandardModalContentNew from 'GeneralUI/StandardModalContent/StandardModalContentNew.jsx'
import StandardModalContent from 'GeneralUI/StandardModalContent/StandardModalContent.jsx'
import withWindowScreenWorkflowConfigs from './withWindowScreenWorkflowConfigs.js'
import InputGroupText from 'GeneralUI/inputGroupText/InputGroupText.jsx'
import { push, encodeItemURIComponents, getContextName } from 'Helper/routeHelpers'
import { pathValidator } from '../../Helper/sysAdminValidators.js'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'
import { openModal, closeModal } from 'actions/modalActions'
import { useSelector, useDispatch } from 'react-redux'
import EditWindowModal from './EditWindowModal.jsx'
import { useLocation } from 'react-router-dom'
import { xtractAPI } from 'actions/apiActions'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import _ from 'lodash'
import {
  getNextAvailablePath,
  getErrorModalAction
} from './DesignerHelpers.jsx'

const EditScreenModal = (props) => {
  const {
    isEdit,
    closeSelf,
    startSpinner,
    endSpinnerWithSuccess,
    endSpinnerWithError,
    contextScreenConfigs,
    contextScreensConfigsId,
  } = props

  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const currentScreenPath = useSelector(state => state.router?.match?.params?.screen)

  const [titleState, setTitleState] = useState("")
  const [urlState, setUrlState] = useState("")
  const [screenMenuState, setScreenMenuState] = useState(true)

  const nextAvailablePath = useMemo(() => {
    return getNextAvailablePath(contextScreenConfigs, 'screen')
  }, [getNextAvailablePath, contextScreenConfigs])

  const currentScreenType = useMemo(getCurrentScreenType, [])

  const currentScreenConfig = useMemo(() => {
    if (isEdit) {
      return contextScreenConfigs.find(screen => screen.path === currentScreenPath && screen.screenType === currentScreenType)
    } else {
      return { name: "", path: nextAvailablePath, selectable: true }
    }
  }, [currentScreenPath, contextScreenConfigs, nextAvailablePath, currentScreenType])

  useEffect(() => {
    if (currentScreenConfig) {
      setTitleState(currentScreenConfig.name)
      setUrlState(currentScreenConfig.path)
      setScreenMenuState(currentScreenConfig.selectable)
    }
  }, [currentScreenConfig])

  useEffect(() => {
    // make api call to ensure screen data loads
    const searchObj = { equal: { name: 'screens' } }
    dispatch(xtractAPI('config', 'search', searchObj))
  }, [])

  const screenContext = useMemo(() => {
    return getContextName(pathname)
  }, [pathname])

  const validateScreens = useCallback((screens) => {
    const validationErrors = pathValidator(screens, 'screen')
    if (validationErrors) {
      const ErrorContent = <div>
        {"Each configured screen must have a unique path. The following errors were detected:"}
        <ul>
          {validationErrors}
        </ul>
      </div>
      dispatch(getErrorModalAction('Validation error', ErrorContent))
      return false
    } else {
      // no errors to handle, we passed
      return true
    }
  }, [getErrorModalAction])

  const isFormValid = useCallback(() => {
    if (!titleState) {
      dispatch(getErrorModalAction('Missing Title', 'Every screen must have a title defined'))
      return false
    }

    return true
  }, [titleState, getErrorModalAction])

  const getNewScreen = useCallback(() => {
    return {
      name: titleState,
      path: urlState,
      selectable: screenMenuState,
      screenType: currentScreenType,
      windows: []
    }
  }, [titleState, urlState, screenMenuState, currentScreenType])

  const saveScreensToApi = useCallback((screens, callback = () => null) => {
    const screenData = JSON.stringify(_.cloneDeep(screens).map(encodeItemURIComponents))
    const callbacks = {
      200: response => {
        endSpinnerWithSuccess()
        callback()
      },
      400: (response, action) => endSpinnerWithError({ action, response, errors: response.data.errors }),
      error: (response, action) => endSpinnerWithError({ response, action })
    }
    startSpinner()

    const config = {
      name: 'screens',
      section: screenContext,
      value: screenData,
      app: 'XIS'
    }

    if (contextScreensConfigsId) {
      dispatch(xtractAPI(`config/${contextScreensConfigsId}`, 'update', config, callbacks))
    } else {
      dispatch(xtractAPI(`config`, 'create', config, callbacks))
    }
  }, [contextScreensConfigsId, startSpinner, endSpinnerWithError, endSpinnerWithSuccess, screenContext])

  const updateScreen = useCallback(() => {
    if (!isFormValid()) {
      return true
    }

    let allScreens = _.cloneDeep(contextScreenConfigs)
    let effectedScreen
    if (isEdit) {
      allScreens = allScreens.map(screen => {
        if (screen.path === urlState && screen.screenType === currentScreenType) {
          screen.name = titleState
          screen.selectable = screenMenuState
        }
        effectedScreen = screen
        return screen
      })
    } else {
      effectedScreen = getNewScreen()
      allScreens = [...allScreens, effectedScreen]
    }
    if (validateScreens(allScreens)) {
      const split = pathname.split('/screen')
      saveScreensToApi(
        allScreens,
        () => {
          push(`${split[0]}/screen/${effectedScreen.path}`)
          if(!isEdit) {
            // if this is a create and not an edit, we should also launch the add window modal
            const closeSelf = () => dispatch(closeModal('edit_window_modal'))
            dispatch(
              openModal('edit_window_modal', {
                title: 'Configure ' + currentScreenType + ' Window',
                content: (
                  <EditWindowModal
                    closeSelf={closeSelf}
                  />
                ),
                closeable: true
              })
            )
          }
        }
      )
    }
  }, [contextScreenConfigs, currentScreenType, titleState, urlState, screenMenuState, pathname, closeModal])

  const deleteScreen = useCallback(() => {
    let allScreens = _.cloneDeep(contextScreenConfigs)
    allScreens = allScreens.filter(screen => !(screen.path === currentScreenPath && screen.screenType === currentScreenType))
    const split = pathname.split('/screen')
    saveScreensToApi(allScreens, () => push(split[0]))
  }, [contextScreenConfigs, currentScreenPath, currentScreenType, pathname])

  const deleteScreenRef = useRef(() => null)
  useEffect(() => {
    deleteScreenRef.current = deleteScreen
  }, [deleteScreen])

  const confirmDeleteModal = useCallback((titleState) => {
    dispatch(openModal('confirm_modal', {
      title: 'Delete Screen',
      content: (
        <StandardModalContent
          messageContent={
            <div style={{ margin: '25px' }}>
              Are you sure you want to delete <b>{titleState ? titleState : ''} </b>screen?
            </div>
          }
          cancelText={'No'}
          cancelHandler={() => false}
          okText={'Yes'}
          okHandler={deleteScreenRef.current}
        />
      ),
      closeable: true
    }))
    return true // leave modal open
  }, [deleteScreenRef.current])

  return (
    <StandardModalContentNew
      cancelHandler={closeSelf}
      okHandler={isEdit ? () => confirmDeleteModal(titleState) : null}
      thirdHandler={updateScreen}
      okText={'Delete'}
      okClassName={'btn-warning'}
      thirdText={'Save'}
      cancelText={'Cancel'}
      closeSelf={closeSelf}
    >
      <div className='edit-screen-modal'>
        <div className='title-input'>
          <InputGroupText
            type="text"
            title="Title"
            placeholder=""
            aria-label={'screen title'}
            value={titleState}
            style={{ textAlign: 'left' }}
            onChange={e => setTitleState(e.target.value)}
          />
        </div>
        <div className='url-input'>
          <InputGroupText
            type="text"
            placeholder=""
            title="URL Name"
            aria-label={'url name'}
            value={urlState}
            style={{ textAlign: 'left' }}
            disabled={isEdit}
            onChange={e => setUrlState(e.target.value)}
          />
        </div>
        <div className='screen-menu-checkbox'>
          <InputGroupText
            type="checkbox"
            placeholder=""
            aria-label={'screen menu'}
            title={"Include in screen menu?"}
            checked={screenMenuState}
            style={{ textAlign: 'left' }}
            onChange={e => setScreenMenuState(e.target.checked)}
          />
        </div>
      </div>
    </StandardModalContentNew>
  )
}

EditScreenModal.propTypes = {
  onUpdate: PropTypes.func,
  closeSelf: PropTypes.func.isRequired,
}

const enhance = compose(
  withWindowScreenWorkflowConfigs()
)

export default enhance(EditScreenModal)
